import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Mail, Phone, Building2, MapPin, Users, Sparkles, Heart, Linkedin, QrCode } from "lucide-react";
import { VIA_TRANSLATIONS_PT } from "./profileConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CIEES_ROLE_LABELS = {
  presidente: "Presidente",
  vice_presidente: "Vice-Presidente",
  presidente_assembleia: "Presidente da Assembleia",
  presidente_conselho_fiscal: "Presidente do Conselho Fiscal",
  direcao_executiva: "Direção Executiva",
  vogal: "Vogal da Direção",
  comunidades: "Comunidades",
  financeiro: "Financeiro",
  tesoureiro: "Tesoureiro(a)",
  formacao: "Educação",
  espiritualidade: "Espiritualidade",
  comunicacao: "Comunicação",
  secretario: "Secretária(o) da Direção",
  assessor_espiritual: "Assessor Espiritual",
  // Legacy aliases (read-only fallback, in case any pre-migration doc remains)
  vice_presidente_comunidades: "Vice-Presidente",
  comunicacao_secretaria: "Comunicação",
};

// MBTI 16 personalities — Portuguese labels.
const MBTI_PT = {
  INTJ: "Arquiteto", INTP: "Lógico", ENTJ: "Comandante", ENTP: "Polemista",
  INFJ: "Defensor", INFP: "Mediador", ENFJ: "Protagonista", ENFP: "Activista",
  ISTJ: "Logístico", ISFJ: "Defensor", ESTJ: "Executivo", ESFJ: "Cônsul",
  ISTP: "Virtuoso", ISFP: "Aventureiro", ESTP: "Empreendedor", ESFP: "Animador",
};

// True when the member's alianca_amor_date (YYYY-MM-DD) anniversary falls in
// the current month.
const isAliancaAnniversaryMonth = (dateIso) => {
  if (!dateIso || dateIso.length < 7) return false;
  const month = parseInt(dateIso.slice(5, 7), 10);
  return month === new Date().getMonth() + 1;
};

export default function MemberDirectory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async (qs = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/members/directory${qs ? `?q=${encodeURIComponent(qs)}` : ""}`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error("Erro a carregar o diretório."); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  return (
    <section className="container-page py-12 lg:py-16" data-testid="member-directory-page">
      <div className="overline">Sócios</div>
      <h1 className="display-heading text-4xl sm:text-5xl mt-3">Diretório.</h1>
      <span className="accent-rule mt-6 block" />
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Conheça os outros sócios do CIEES Portugal — empresa, função, perfil de personalidade e valores.
        Pode controlar a sua visibilidade em <strong>Perfil → Privacidade</strong>.
      </p>

      <form onSubmit={onSearch} className="mt-8 flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar por nome, empresa ou cidade…"
            className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-[hsl(var(--border))] focus:outline-none focus:border-[hsl(var(--accent))]"
            data-testid="directory-search-input"
          />
        </div>
        <button type="submit" className="px-4 py-2.5 text-xs uppercase tracking-[0.18em] bg-[hsl(var(--primary))] text-white hover:bg-[#093630] transition-colors"
          data-testid="directory-search-submit">
          Procurar
        </button>
      </form>

      <div className="mt-10">
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-12 text-center" data-testid="directory-empty">
            <Users className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              Ainda não há sócios visíveis no diretório.
            </p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
            {items.map((m) => (
              <li key={m.id} className="bg-white border border-[hsl(var(--border))] p-5 hover:border-[hsl(var(--accent))] transition-colors flex flex-col" data-testid={`directory-card-${m.id}`}>
                <div className="flex items-start gap-3">
                  {m.picture ? (
                    <img src={m.picture} alt={m.name} className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-base font-medium">
                      {(m.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="display-heading text-lg leading-tight truncate">{m.name}</h3>
                    {m.ciees_role && (
                      <span
                        className="inline-block mt-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[hsl(var(--primary))] text-[hsl(var(--accent))] rounded-sm"
                        data-testid={`directory-role-${m.id}`}
                      >
                        {CIEES_ROLE_LABELS[m.ciees_role] || m.ciees_role}
                      </span>
                    )}
                    {m.role && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{m.role}</p>}
                  </div>
                </div>

                <dl className="mt-4 space-y-1.5 text-xs">
                  {m.company && <Field icon={Building2}>{m.company}</Field>}
                  {(m.city || m.country) && <Field icon={MapPin}>{[m.city, m.country].filter(Boolean).join(", ")}</Field>}
                  {m.email && <Field icon={Mail}><a href={`mailto:${m.email}`} className="hover:text-[hsl(var(--primary))]">{m.email}</a></Field>}
                  {m.phone && <Field icon={Phone}><a href={`tel:${m.phone}`} className="hover:text-[hsl(var(--primary))]">{m.phone}</a></Field>}
                </dl>

                {(m.linkedin || m.card_slug) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m.linkedin && (
                      <a
                        href={m.linkedin} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0A66C2]/30 text-[#0A66C2] text-[11px] uppercase tracking-[0.14em] hover:bg-[#0A66C2] hover:text-white transition-colors"
                        data-testid={`directory-linkedin-${m.id}`}
                      >
                        <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                      </a>
                    )}
                    {m.card_slug && (
                      <a
                        href={`/cartao/${m.card_slug}`} target="_blank" rel="noopener noreferrer"
                        title="Cartão de visita digital"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--border))] text-[hsl(var(--primary))] text-[11px] uppercase tracking-[0.14em] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
                        data-testid={`directory-cartao-${m.id}`}
                      >
                        <QrCode className="h-3.5 w-3.5" /> Cartão
                      </a>
                    )}
                  </div>
                )}

                {/* Personality + Values */}
                {(m.mbti || (m.via_values && m.via_values.length > 0)) && (
                  <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] space-y-2" data-testid={`directory-personality-${m.id}`}>
                    {m.mbti && (
                      <div className="flex items-center gap-2 text-xs">
                        <Sparkles className="h-3 w-3 text-[hsl(var(--accent))] flex-shrink-0" />
                        <span className="font-mono font-semibold text-[hsl(var(--primary))]">{m.mbti}</span>
                        {MBTI_PT[m.mbti] && <span className="text-[hsl(var(--muted-foreground))]">· {MBTI_PT[m.mbti]}</span>}
                      </div>
                    )}
                    {m.via_values && m.via_values.length > 0 && (
                      <div className="flex items-start gap-2 text-xs">
                        <Heart className="h-3 w-3 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                        <div className="flex flex-wrap gap-1">
                          {m.via_values.slice(0, 5).map((v) => (
                            <span key={v} className="px-1.5 py-0.5 bg-[hsl(var(--secondary))] text-[10px] text-[hsl(var(--foreground))] rounded-sm">
                              {VIA_TRANSLATIONS_PT[v] || v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {m.ideal_pessoal && (
                  <p className="mt-4 pt-4 border-t border-[hsl(var(--border))] text-xs italic text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3" data-testid={`directory-ideal-${m.id}`}>
                    “{m.ideal_pessoal}”
                  </p>
                )}

                {m.alianca_amor && (
                  <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent))]" data-testid={`directory-alianca-${m.id}`}>
                    <span className="text-[hsl(var(--muted-foreground))] normal-case tracking-normal">Aliança de Amor · </span>
                    <span className="normal-case tracking-normal text-[hsl(var(--foreground))]">{m.alianca_amor}</span>
                    {isAliancaAnniversaryMonth(m.alianca_amor_date) && (
                      <span
                        className="inline-flex items-center gap-1 ml-2 align-middle normal-case tracking-normal text-[10px] text-[hsl(var(--accent))]"
                        title="Celebra o aniversário da Aliança de Amor este mês"
                        data-testid={`directory-alianca-anniversary-${m.id}`}
                      >
                        <Heart className="h-3 w-3 fill-current" />
                        aniversário este mês
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const Field = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
    <Icon className="h-3 w-3 flex-shrink-0" />
    <span className="truncate">{children}</span>
  </div>
);
